import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, Alert, Layout, Space } from 'antd';
import { MailOutlined, LockOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginUser, LoginCredentials } from '../services/authApi';
import { useAppDispatch, useAppSelector } from '../store';
import { loginStart, loginSuccess, loginFailure, clearError } from '../store/userSlice';
import '../styles.css';

const { Title, Text } = Typography;
const { Content } = Layout;

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { error, isAuthenticated } = useAppSelector(state => state.user);
  
  useEffect(() => {
    if (isAuthenticated) {
      interface LocationState {
        from: {
          pathname: string;
        };
      }
      const from = (location.state as LocationState)?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
    
    return () => {
      if (error) dispatch(clearError());
    };
  }, [isAuthenticated, navigate, location, dispatch, error]);
  
  const handleSubmit = async (values: LoginCredentials) => {
    setLoading(true);
    dispatch(loginStart());
    
    try {
      const userData = await loginUser(values);
      dispatch(loginSuccess(userData));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(loginFailure(error.message));
      } else {
        dispatch(loginFailure('Échec de connexion, veuillez réessayer.'));
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Layout style={{ minHeight: '100vh', background: '#fff' }}>
      <Content style={{ display: 'flex', minHeight: '100vh' }}>
        {/* Left Side: Branding/Illustration */}
        <div style={{ 
          flex: '1', 
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px',
          color: '#fff',
          position: 'relative',
          overflow: 'hidden'
        }} className="login-branding">
          {/* Abstract circles for modern look */}
          <div style={{
            position: 'absolute',
            top: '-10%',
            right: '-10%',
            width: '400px',
            height: '400px',
            background: 'rgba(56, 189, 248, 0.1)',
            borderRadius: '50%',
            filter: 'blur(80px)'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '10%',
            left: '0',
            width: '300px',
            height: '300px',
            background: 'rgba(139, 92, 246, 0.1)',
            borderRadius: '50%',
            filter: 'blur(80px)'
          }} />

          <div style={{ zIndex: 1, textAlign: 'center', maxWidth: '480px' }}>
            <img 
              src="../../public/monogramme-honnore-blanc.png" 
              alt="Honnoré Services" 
              style={{ height: '100px', marginBottom: '32px', filter: 'brightness(0) invert(1)' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <Title level={1} style={{ color: '#fff', marginBottom: '16px', fontWeight: 800, fontSize: '3rem' }}>
              Honnoré Services
            </Title>
            <Title level={4} style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 400, marginBottom: '40px' }}>
              L'excellence opérationnelle au service de votre logistique et de votre croissance.
            </Title>
            
            <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'left' }}>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: '12px', borderRadius: '12px' }}>
                  <ArrowRightOutlined style={{ color: '#38bdf8' }} />
                </div>
                <div>
                  <Text strong style={{ color: '#fff', display: 'block' }}>Pilotage Supply Chain</Text>
                  <Text style={{ color: 'rgba(255,255,255,0.5)' }}>Gestion multi-entrepôts et flux fournisseurs.</Text>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: '12px', borderRadius: '12px' }}>
                  <ArrowRightOutlined style={{ color: '#38bdf8' }} />
                </div>
                <div>
                  <Text strong style={{ color: '#fff', display: 'block' }}>Intelligence Artificielle</Text>
                  <Text style={{ color: 'rgba(255,255,255,0.5)' }}>Automatisation des suivis et aide à la décision.</Text>
                </div>
              </div>
            </Space>
          </div>
          
          <div style={{ position: 'absolute', bottom: '40px', color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>
            © {new Date().getFullYear()} Honnoré Services. Tous droits réservés.
          </div>
        </div>

        {/* Right Side: Login Form */}
        <div style={{ 
          width: '500px', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          padding: '80px',
          background: '#fff'
        }}>
          <div style={{ maxWidth: '360px', width: '100%', margin: '0 auto' }}>
            <Title level={2} style={{ marginBottom: '8px', fontWeight: 700 }}>Bon retour</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: '32px' }}>
              Veuillez entrer vos identifiants pour accéder au portail.
            </Text>
            
            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                style={{ marginBottom: 24, borderRadius: '8px' }}
                closable
                onClose={() => dispatch(clearError())}
              />
            )}
            
            <Form
              form={form}
              name="login"
              layout="vertical"
              onFinish={handleSubmit}
              requiredMark={false}
              size="large"
            >
              <Form.Item
                label="Adresse Email"
                name="email"
                rules={[
                  { required: true, message: 'Veuillez entrer votre email' },
                  { type: 'email', message: 'Format d\'email invalide' }
                ]}
              >
                <Input 
                  prefix={<MailOutlined style={{ color: '#94a3b8' }} />} 
                  placeholder="nom@honnore.com" 
                  style={{ borderRadius: '8px' }}
                />
              </Form.Item>
              
              <Form.Item
                label="Mot de passe"
                name="password"
                rules={[{ required: true, message: 'Veuillez entrer votre mot de passe' }]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="••••••••"
                  style={{ borderRadius: '8px' }}
                />
              </Form.Item>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
                <Button type="link" size="small" style={{ color: '#64748b', padding: 0 }}>
                  Mot de passe oublié ?
                </Button>
              </div>
              
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  style={{ 
                    height: '48px', 
                    borderRadius: '8px', 
                    fontSize: '16px', 
                    fontWeight: 600,
                    background: '#0f172a',
                    borderColor: '#0f172a',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  Se connecter
                </Button>
              </Form.Item>
            </Form>

            <div style={{ textAlign: 'center', marginTop: '40px' }}>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                Besoin d'aide ? <Button type="link" size="small" style={{ padding: 0 }}>Contacter le support</Button>
              </Text>
            </div>
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default Login;
